"use client";

import { motion } from "framer-motion";
import { useLang } from "@/contexts/language-context";
import { Check, ChefHat, Timer } from "lucide-react";

export default function OnlineOrderingFlowScroll() {
  const { t } = useLang();

  return (
    <section className="relative py-32 px-6 bg-[#FAFAF8] overflow-hidden">
      <div className="max-w-[1280px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="text-center mb-20 max-w-[760px] mx-auto"
        >
          <div className="text-[11px] font-black tracking-widest uppercase mb-4" style={{ color: "#D7263D" }}>
            {t("From thumb to kitchen", "Del pulgar a la cocina")}
          </div>
          <h2 className="text-4xl md:text-[64px] font-black tracking-[-0.03em] leading-[1.02] text-redy-charcoal">
            {t("Three taps. ", "Tres taps. ")}
            <span className="text-redy-charcoal/30">{t("In seconds.", "En segundos.")}</span>
          </h2>
          <p className="text-[16px] font-medium text-redy-secondary leading-relaxed mt-6">
            {t(
              "Friction kills orders. We removed it. The customer goes from your site to your kitchen without an app install, an account creation, or a 4-page checkout.",
              "La fricción mata pedidos. La quitamos. El cliente va de tu sitio a tu cocina sin instalar app, sin crear cuenta, sin checkout de 4 pasos."
            )}
          </p>
        </motion.div>

        {/* 3-step grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-6">
          {/* Step 1 — iPhone browsing menu */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, delay: 0.05, ease: [0.21, 0.47, 0.32, 0.98] }}
            className="flex flex-col items-center"
          >
            <div className="mb-6 relative">
              <IPhoneFrame>
                {/* Browser-style site mock */}
                <div className="bg-white h-full flex flex-col">
                  {/* Status bar */}
                  <div className="h-7 bg-[#FAFAF8] flex items-center justify-between px-4 text-[8px] font-bold text-redy-charcoal">
                    <span>6:42</span>
                    <span>5G</span>
                  </div>
                  {/* URL bar */}
                  <div className="px-3 py-1.5 bg-[#F0EDE8]">
                    <div className="bg-white rounded-full px-3 py-1 text-[9px] text-redy-charcoal/60 truncate">
                      pepitos.useredy.com
                    </div>
                  </div>
                  {/* Hero */}
                  <div className="px-3 py-3 bg-redy-charcoal text-white">
                    <div className="text-[11px] font-black">Pepitos WAAO</div>
                    <div className="text-[8px] text-white/60">{t("Order pickup · 15 min", "Pickup · 15 min")}</div>
                  </div>
                  {/* Menu items */}
                  <div className="flex-1 px-3 py-2.5 space-y-2 bg-white">
                    {[
                      { name: "Birria Plate", price: "$16.50", added: false },
                      { name: "Quesabirria × 2", price: "$12.00", added: true },
                      { name: "Agua Fresca", price: "$4.00", added: true },
                      { name: "Elote Loco", price: "$5.50", added: false },
                    ].map((item, i) => (
                      <div key={i} className="flex items-center justify-between gap-2 pb-1.5 border-b border-black/5">
                        <div className="flex-1 min-w-0">
                          <div className="text-[9px] font-bold text-redy-charcoal truncate">{item.name}</div>
                          <div className="text-[8px] text-redy-secondary tabular-nums">{item.price}</div>
                        </div>
                        <div
                          className={`w-5 h-5 rounded-full flex items-center justify-center text-white text-[10px] font-black ${
                            item.added ? "" : "bg-redy-charcoal/10 text-redy-charcoal/40"
                          }`}
                          style={item.added ? { backgroundColor: "#F4722B" } : undefined}
                        >
                          {item.added ? <Check className="w-2.5 h-2.5" strokeWidth={3.5} /> : "+"}
                        </div>
                      </div>
                    ))}
                  </div>
                  {/* Bottom cart bar */}
                  <div className="px-3 py-2 text-white text-center text-[9px] font-bold" style={{ background: "linear-gradient(135deg, #F4722B 0%, #D7263D 100%)" }}>
                    {t("Cart · 2 items · $16.00 →", "Carrito · 2 items · $16.00 →")}
                  </div>
                </div>
              </IPhoneFrame>
            </div>
            <StepBadge n={1} color="#D7263D" />
            <div className="text-[18px] md:text-[20px] font-black tracking-tight text-redy-charcoal mt-4 mb-2 text-center">
              {t("Your site. Their thumb.", "Tu sitio. Su pulgar.")}
            </div>
            <p className="text-[13px] font-medium text-redy-secondary text-center max-w-[260px] leading-relaxed">
              {t(
                "Direct link from Instagram, Google or a table QR. The menu loads fast.",
                "Link directo desde Instagram, Google o QR en mesa. El menú carga rápido."
              )}
            </p>
          </motion.div>

          {/* Step 2 — Apple Pay sheet */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, delay: 0.2, ease: [0.21, 0.47, 0.32, 0.98] }}
            className="flex flex-col items-center"
          >
            <div className="mb-6 relative">
              <IPhoneFrame>
                <div className="bg-redy-charcoal/90 h-full flex flex-col relative">
                  {/* Status bar */}
                  <div className="h-7 bg-redy-charcoal flex items-center justify-between px-4 text-[8px] font-bold text-white">
                    <span>6:42</span>
                    <span>5G</span>
                  </div>
                  {/* Dim background of site */}
                  <div className="flex-1 px-3 py-3 space-y-1.5 opacity-30">
                    <div className="h-2 bg-white/30 rounded w-20" />
                    <div className="h-1.5 bg-white/20 rounded w-32" />
                    <div className="h-1.5 bg-white/20 rounded w-24" />
                  </div>
                  {/* Apple Pay sheet emerging */}
                  <motion.div
                    initial={{ y: 120 }}
                    whileInView={{ y: 0 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ duration: 0.7, delay: 0.5, ease: [0.21, 0.47, 0.32, 0.98] }}
                    className="bg-white rounded-t-[20px] px-4 pt-3 pb-4 shadow-2xl"
                  >
                    <div className="w-8 h-1 bg-black/10 rounded-full mx-auto mb-3" />
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-[8px] font-bold uppercase tracking-wider text-redy-secondary">{t("Pay", "Pagar")}</span>
                      <svg width="36" height="14" viewBox="0 0 36 14" fill="black">
                        <text x="0" y="11" fontSize="11" fontWeight="700" fontFamily="-apple-system,sans-serif">
                          Pay
                        </text>
                      </svg>
                    </div>
                    <div className="border-t border-black/5 pt-2.5 mb-3">
                      <div className="flex justify-between text-[10px]">
                        <span className="text-redy-secondary">Pepitos WAAO</span>
                        <span className="font-black tabular-nums text-redy-charcoal">$32.50</span>
                      </div>
                    </div>
                    {/* Face ID indicator — semantic green (redy-green from palette) */}
                    <div className="flex flex-col items-center gap-1.5 py-1.5">
                      <motion.div
                        initial={{ scale: 0.5, opacity: 0 }}
                        whileInView={{ scale: 1, opacity: 1 }}
                        viewport={{ once: true, margin: "-100px" }}
                        transition={{ duration: 0.4, delay: 1.2 }}
                        className="w-7 h-7 rounded-full flex items-center justify-center"
                        style={{ backgroundColor: "#2ECC71" }}
                      >
                        <Check className="w-4 h-4 text-white" strokeWidth={3.5} />
                      </motion.div>
                      <div className="text-[8px] font-bold uppercase tracking-wider" style={{ color: "#2ECC71" }}>
                        {t("Done", "Listo")}
                      </div>
                    </div>
                  </motion.div>
                </div>
              </IPhoneFrame>
            </div>
            <StepBadge n={2} color="#D7263D" />
            <div className="text-[18px] md:text-[20px] font-black tracking-tight text-redy-charcoal mt-4 mb-2 text-center">
              {t("Apple Pay. Done.", "Apple Pay. Listo.")}
            </div>
            <p className="text-[13px] font-medium text-redy-secondary text-center max-w-[260px] leading-relaxed">
              {t(
                "No account. No password. No \"create profile\" friction. Face ID and the order is paid.",
                "Sin cuenta. Sin password. Sin fricción de \"crear perfil\". Face ID y el pedido está pagado."
              )}
            </p>
          </motion.div>

          {/* Step 3 — KDS ticket */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, delay: 0.35, ease: [0.21, 0.47, 0.32, 0.98] }}
            className="flex flex-col items-center"
          >
            <div className="mb-6 relative">
              <KdsFrame>
                <div className="bg-[#0a1f1c] h-full p-3 text-white">
                  {/* Header */}
                  <div className="flex justify-between items-center mb-3 pb-2 border-b border-white/10">
                    <div className="text-[9px] font-black uppercase tracking-wider text-white/40">
                      KDS · {t("Cocina", "Cocina")}
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ backgroundColor: "#F4722B" }} />
                      <span className="text-[8px] font-bold uppercase tracking-wider" style={{ color: "#F4722B" }}>
                        Live
                      </span>
                    </div>
                  </div>

                  {/* Incoming ticket */}
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9, y: -10 }}
                    whileInView={{ opacity: 1, scale: 1, y: 0 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ duration: 0.5, delay: 0.5 }}
                    className="rounded-[10px] p-2.5 mb-2"
                    style={{ backgroundColor: "rgba(244, 114, 43, 0.12)", border: "1px solid rgba(244, 114, 43, 0.4)" }}
                  >
                    <div className="flex justify-between items-center mb-1.5">
                      <div className="text-[10px] font-black text-white">#104</div>
                      <div
                        className="text-[8px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded"
                        style={{ backgroundColor: "#F4722B", color: "white" }}
                      >
                        Pickup
                      </div>
                    </div>
                    <div className="space-y-0.5 text-[9px] text-white/85">
                      <div>1× Birria Plate</div>
                      <div>2× Quesabirria</div>
                      <div>1× Agua Fresca</div>
                    </div>
                    <div className="mt-2 pt-1.5 border-t border-white/10 flex items-center justify-between">
                      <div className="flex items-center gap-1 text-[8px] text-white/60">
                        <Timer className="w-2.5 h-2.5" />
                        <span>6:45 PM</span>
                      </div>
                      <div className="text-[8px] font-bold uppercase tracking-wider" style={{ color: "#F4722B" }}>
                        {t("New", "Nuevo")}
                      </div>
                    </div>
                  </motion.div>

                  {/* Other tickets (dim) */}
                  <div className="rounded-[10px] p-2 mb-1.5 bg-white/[0.03] border border-white/5 opacity-60">
                    <div className="flex justify-between text-[9px] text-white/50">
                      <span>#103 · Table 7</span>
                      <span>3m</span>
                    </div>
                  </div>
                  <div className="rounded-[10px] p-2 bg-white/[0.03] border border-white/5 opacity-40">
                    <div className="flex justify-between text-[9px] text-white/50">
                      <span>#102 · Table 4</span>
                      <span>7m</span>
                    </div>
                  </div>
                </div>
              </KdsFrame>
            </div>
            <StepBadge n={3} color="#D7263D" />
            <div className="text-[18px] md:text-[20px] font-black tracking-tight text-redy-charcoal mt-4 mb-2 text-center">
              {t("Lands on the KDS.", "Cae en el KDS.")}
            </div>
            <p className="text-[13px] font-medium text-redy-secondary text-center max-w-[260px] leading-relaxed">
              {t(
                "The kitchen doesn't even know it came from online. Same ticket. Same flow. Pickup tag in green.",
                "La cocina ni sabe que vino de online. Mismo ticket. Mismo flujo. Tag de pickup en verde."
              )}
            </p>
          </motion.div>
        </div>

        {/* Caption */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="text-center mt-16"
        >
          <div className="inline-flex items-center gap-2 bg-white px-5 py-3 rounded-full border border-black/5 shadow-sm">
            <ChefHat className="w-4 h-4" style={{ color: "#D7263D" }} />
            <span className="text-[13px] font-bold text-redy-charcoal">
              {t(
                "No new screen for the team. No new app to learn.",
                "Sin pantalla nueva para el equipo. Sin app nueva que aprender."
              )}
            </span>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function StepBadge({ n, color }: { n: number; color: string }) {
  return (
    <div
      className="w-7 h-7 rounded-full flex items-center justify-center text-white text-[11px] font-black"
      style={{ backgroundColor: color }}
    >
      {n}
    </div>
  );
}

function IPhoneFrame({ children }: { children: React.ReactNode }) {
  return (
    <div className="w-[180px] h-[360px] bg-[#0a0a0a] rounded-[28px] p-[5px] shadow-[0_30px_60px_rgba(0,0,0,0.18)]">
      <div className="w-full h-full rounded-[24px] overflow-hidden bg-white relative">
        {/* Notch */}
        <div className="absolute top-1.5 left-1/2 -translate-x-1/2 w-16 h-3 bg-[#0a0a0a] rounded-full z-10" />
        {children}
      </div>
    </div>
  );
}

function KdsFrame({ children }: { children: React.ReactNode }) {
  return (
    <div className="w-[220px] h-[360px] bg-[#0a0a0a] rounded-[16px] p-[6px] shadow-[0_30px_60px_rgba(0,0,0,0.18)]">
      <div className="w-full h-full rounded-[10px] overflow-hidden bg-[#0a1f1c]">
        {children}
      </div>
    </div>
  );
}
